import React, { useEffect, useState } from "react";

export default function AuthForgot() {
  const [medecins, setMedecins] = useState([]);
  const [selected, setSelected] = useState("");
  const [email, setEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch("/api/v2/auth/public/recovery-medecins", { credentials: "omit" });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.ok) {
          setErrMsg(json?.error || "Impossible de charger la liste des médecins.");
          return;
        }
        setMedecins(Array.isArray(json.medecins) ? json.medecins : []);
      } catch {
        setErrMsg("Impossible de charger la liste des médecins. Réessayez dans quelques instants.");
      }
    }
    load();
  }, []);

  function parseSelected(v) {
    const [nom, prenom] = String(v || "").split("||");
    return { nom: nom || "", prenom: prenom || "" };
  }

  async function onSubmit(e) {
    e.preventDefault();
    setOkMsg("");
    setErrMsg("");

    const { nom, prenom } = parseSelected(selected);
    if (!nom || !prenom) {
      setErrMsg("Veuillez sélectionner votre nom dans la liste.");
      return;
    }
    if (!email.trim()) {
      setErrMsg("Veuillez renseigner votre email.");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/v2/auth/request-password-reset", {
        method: "POST",
        credentials: "omit",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nom: nom.trim(),
          prenom: prenom.trim(),
          email: email.trim(),
        }),
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.ok) {
        setLoading(false);
        setErrMsg(json?.error || "Impossible d'envoyer le lien.");
        return;
      }

      setLoading(false);
      setOkMsg(json?.message || "Si un compte correspond, un message est envoyé à l'adresse enregistrée.");
    } catch {
      setLoading(false);
      setErrMsg("Erreur réseau.");
    }
  }

  const S = {
    container: {
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#f3f4f6",
      padding: "clamp(24px, 4dvh, 48px) 28px",
      fontFamily:
        "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    card: {
      width: "clamp(720px, 52vw, 1100px)",
      maxWidth: "calc(100vw - 56px)",
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: "24px",
      padding: "clamp(38px, 4vw, 54px)",
      boxShadow: "0 22px 56px rgba(15,23,42,0.12)",
    },
    h1: { margin: 0, fontSize: "40px", fontWeight: 950, color: "#111827" },
    p: { marginTop: "12px", color: "#4b5563", lineHeight: 1.6, fontSize: "16px" },
    form: { marginTop: "22px", display: "grid", gap: "16px" },
    label: { fontSize: "15px", fontWeight: 800, color: "#111827" },
    input: {
      width: "100%",
      padding: "15px 14px",
      borderRadius: "12px",
      border: "1px solid #d1d5db",
      outline: "none",
      fontSize: "16px",
      background: "#fff",
    },
    row: { display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "14px" },
    btn: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #111827",
      background: "#111827",
      color: "#fff",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    btnLight: {
      padding: "15px 22px",
      borderRadius: "9999px",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#111827",
      fontSize: "15px",
      fontWeight: 900,
      cursor: "pointer",
    },
    alertOk: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
      fontSize: "14px",
      lineHeight: 1.5,
      wordBreak: "break-word",
    },
    alertErr: {
      marginTop: "14px",
      padding: "12px",
      borderRadius: "12px",
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.5,
    },
    small: { fontSize: "13px", color: "#6b7280", marginTop: "8px" },
  };

  return (
    <div style={S.container}>
      <div style={S.card}>
        <h1 style={S.h1}>Mot de passe oublié</h1>
        <p style={S.p}>
          Sélectionnez votre nom dans la liste et indiquez l'adresse email utilisée
          à l'inscription. Si un compte correspond, un lien de réinitialisation sera
          envoyé à l'adresse enregistrée.
        </p>

        <form style={S.form} onSubmit={onSubmit}>
          <div>
            <div style={S.label}>Médecin</div>
            <select
              style={S.input}
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">— Sélectionner —</option>
              {medecins.map((m, i) => {
                const nom = String(m?.nom || "").trim();
                const prenom = String(m?.prenom || "").trim();
                const v = `${nom}||${prenom}`;
                return (
                  <option key={`${v}-${i}`} value={v}>
                    {nom.toUpperCase()} — {prenom}
                  </option>
                );
              })}
            </select>
            <div style={S.small}>
              Si vous ne trouvez pas votre nom : contactez l’administrateur.
            </div>
          </div>

          <div>
            <div style={S.label}>Email</div>
            <input
              style={S.input}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Adresse email utilisée à l'inscription"
            />
          </div>

          <div style={S.row}>
            <button type="submit" style={S.btn} disabled={loading}>
              {loading ? "Envoi..." : "Envoyer le lien"}
            </button>
            <button
              type="button"
              style={S.btnLight}
              onClick={() => { window.location.href = "/auth"; }}
            >
              Retour
            </button>
          </div>
        </form>

        {errMsg ? <div style={S.alertErr}><b>{errMsg}</b></div> : null}
        {okMsg ? <div style={S.alertOk}><b>{okMsg}</b></div> : null}
      </div>
    </div>
  );
}
